import {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  ListItem,
} from '@material-ui/core';
import {Create as CreateIcon, Delete as DeleteIcon} from '@material-ui/icons';
import {getIntegerChangeHandler} from '@ozgene/gp5.utils.data-functions';
import ReactQuill from 'react-quill';
import Dropzone from 'react-dropzone';
import {toggleArrayInclusion} from '@ozgene/gp5.utils.array-functions';
import SelectSbs from 'Sbs/components/SelectSbs';
import Media from 'SbsSuite/Presentation/Media';
import SelectStepType from 'StepUpdateCreate/Components/SelectStepType';
import SelectStepLevel from 'StepUpdateCreate/Components/SelectStepLevel';
import {useStyles} from 'Utility/styles';
import {findStepsBySbs, getBadges} from 'StepUpdateCreate/service/StepService';
import CreateUpdateStepConsumableDialog from 'Consumables/views/CreateUpdateStepConsumableDialog';
import {calculateTotalSbsTime} from 'Sbs/service/SbsService';

const modules = {
  syntax: true,
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{color: []}, {background: []}],
    [{list: 'ordered'}, {list: 'bullet'}, {indent: '-1'}, {indent: '+1'}],
    [{script: 'sub'}, {script: 'super'}],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};
const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'indent',
  'script',
  'blockquote',
  'code-block',
  'link',
];

export default function SbsEditor(props) {
  const {entity, onPropertyChange, onValidityChange} = props;
  const {
    id,
    stepType,
    stepLevel,
    hh = 0,
    mm = 0,
    ss = 0,
    subSbs = null,
    instruction,
    detail = '',
    badges = [],
    stepConsumableUnits,
    media = null,
  } = entity;
  const classes = useStyles();
  const [selectedStepConsumable, setSeelctedStepConsumable] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editStepConsumableDialogOpen, setEditStepConsumableDialogOpen] =
    useState(false);
  const handleChangeHh = getIntegerChangeHandler('hh', onPropertyChange);
  const handleChangeMm = getIntegerChangeHandler('mm', onPropertyChange);
  const handleChangeSs = getIntegerChangeHandler('ss', onPropertyChange);
  const handleChangeInstruction = (instruction) =>
    onPropertyChange({instruction});
  const handleChangeDetail = (detail) => onPropertyChange({detail});
  const handleChangeStepType = (type) => {
    if (stepType?.type === 'Sub SBS' && type?.type !== 'sub SBS') {
      handleChangeSubSbs(null);
    }
    onPropertyChange({stepType: type});
  };
  const handleChangeStepLevel = (stepLevel) => onPropertyChange({stepLevel});
  const handleChangeSubSbs = async (subSbs) => {
    let hh = 0;
    let mm = 0;
    let ss = 0;

    if (!!subSbs) {
      const {id: subSbsId} = subSbs;
      const steps = await findStepsBySbs(subSbsId);

      ({hh, mm, ss} = calculateTotalSbsTime(steps));
    }
    onPropertyChange({hh, mm, ss, subSbs});
  };
  const handleDeleteStepConsumable = (stepConsumableId) =>
    onPropertyChange({
      stepConsumableUnits: stepConsumableUnits.filter(
        (unit) => unit.id !== stepConsumableId,
      ),
    });
  const handleOpenEditStepConsumableDialog = (stepConsumable) => {
    setSeelctedStepConsumable(stepConsumable);
    setEditStepConsumableDialogOpen(true);
  };
  const handleCloseEditStepConsumableDialog = () => {
    setSeelctedStepConsumable(null);
    setEditStepConsumableDialogOpen(false);
  };
  const handleEditStepConsumable = (stepConsumable) => {
    const newStepConsumables = stepConsumableUnits.filter(
      ({id}) => id !== stepConsumable.id,
    );

    onPropertyChange({
      stepConsumableUnits: [...newStepConsumables, stepConsumable],
    });
  };
  const handleDropMedia = (media) => onPropertyChange({media: media[0]});
  const handleChangeBadges = (badge) => {
    const {id: badgeId} = badge;
    const element = badges.findIndex(({id}) => id === badgeId);

    onPropertyChange({
      badges: toggleArrayInclusion(badges, badges[element] || badge),
    });
  };

  useEffect(() => {
    const handleRetrieveBadges = async () => {
      const newBadges = await getBadges();

      setAllBadges(newBadges);
      setIsLoading(false);
    };

    if (isLoading) {
      handleRetrieveBadges();
    }
  }, [isLoading]);
  useEffect(
    () =>
      onValidityChange(
        stepType?.type === 'Sub SBS'
          ? !!subSbs
          : true && !!stepType && !!stepLevel,
      ),
    [onValidityChange, stepLevel, stepType, subSbs],
  );

  return (
    !isLoading && (
      <>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <SelectStepType
                  useDefault={true}
                  value={stepType}
                  onChange={handleChangeStepType}
                  disabled={!!id}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectStepLevel
                  value={stepLevel}
                  onChange={handleChangeStepLevel}
                />
              </Grid>

              {stepType?.type === 'Sub SBS' && (
                <Grid item xs={12}>
                  <SelectSbs
                    label="Sub SBS"
                    onChange={handleChangeSubSbs}
                    value={subSbs}
                    error={!subSbs}
                    errorText={!subSbs ? 'Please select Sub-SBS' : ''}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle1">Instruction</Typography>
                <ReactQuill
                  value={instruction || ''}
                  onChange={handleChangeInstruction}
                  modules={modules}
                  formats={formats}
                />
                <Typography variant="subtitle1">Detail</Typography>
                <ReactQuill
                  value={detail || ''}
                  onChange={handleChangeDetail}
                  modules={modules}
                  formats={formats}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Badges</Typography>
                {allBadges?.map((badge) => (
                  <button
                    key={badge.id}
                    type="button"
                    onClick={() => handleChangeBadges(badge)}
                    style={{
                      backgroundColor: 'Transparent',
                      backgroundRepeat: 'no-repeat',
                      border: 'none',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      outline: 'none',
                    }}
                  >
                    <img
                      alt="badge"
                      key={badge.id}
                      style={{
                        margin: '4px',
                        opacity:
                          badges?.findIndex((item) => item.id === badge.id) > -1
                            ? 1
                            : 0.3,
                      }}
                      src={badge.src}
                    />
                  </button>
                ))}
              </Grid>

              <Grid item xs={12}>
                {stepConsumableUnits?.length > 0 &&
                  stepConsumableUnits.map((eachStepConsumable) => (
                    <Grid
                      container
                      spacing={2}
                      className={classes.aceCell}
                      key={eachStepConsumable.id}
                    >
                      <Grid item xs={9}>
                        <Typography style={{marginTop: 12}}>
                          {`${eachStepConsumable.consumable.name} -- ${eachStepConsumable.quantity} ${eachStepConsumable.metricUnit.name} -- Product Code: ${eachStepConsumable.consumable.productCode}, Supplier: ${eachStepConsumable.consumable.supplier}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <Tooltip title="Edit Quantity">
                          <IconButton
                            onClick={() =>
                              handleOpenEditStepConsumableDialog(
                                eachStepConsumable,
                              )
                            }
                            color="secondary"
                          >
                            <CreateIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={1}>
                        <Tooltip title="Remove Consumable">
                          <IconButton
                            onClick={() =>
                              handleDeleteStepConsumable(eachStepConsumable.id)
                            }
                            color="secondary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <TextField
                  id="hh"
                  label="Hours"
                  value={hh}
                  onChange={handleChangeHh}
                  type="number"
                  inputProps={{min: 0}}
                  fullWidth
                  disabled={stepType?.type === 'Sub SBS'}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="mm"
                  label="Minutes"
                  value={mm}
                  onChange={handleChangeMm}
                  type="number"
                  inputProps={{min: 0}}
                  fullWidth
                  disabled={stepType?.type === 'Sub SBS'}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  id="ss"
                  label="Seconds"
                  value={ss}
                  onChange={handleChangeSs}
                  type="number"
                  inputProps={{min: 0}}
                  fullWidth
                  disabled={stepType?.type === 'Sub SBS'}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1">Upload Media</Typography>
                <Dropzone
                  onDrop={handleDropMedia}
                  style={{
                    borderColor: 'black',
                    borderRadius: 4,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    marginBottom: '4px',
                    padding: '10px',
                  }}
                  multiple={false}
                >
                  {({getRootProps, getInputProps}) => (
                    <section>
                      <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <ListItem button>
                          Drop or click here to upload a file.
                        </ListItem>
                      </div>
                    </section>
                  )}
                </Dropzone>
                {media && (
                  <>
                    <Typography variant="subtitle1">Existing Media</Typography>
                    <Media media={media} />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {editStepConsumableDialogOpen && (
          <CreateUpdateStepConsumableDialog
            entity={selectedStepConsumable}
            onSave={handleEditStepConsumable}
            onClose={handleCloseEditStepConsumableDialog}
          />
        )}
      </>
    )
  );
}

SbsEditor.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    stepType: PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
    }),
    stepLevel: PropTypes.shape({
      id: PropTypes.string,
      level: PropTypes.string,
    }),
    hh: PropTypes.number,
    mm: PropTypes.number,
    ss: PropTypes.number,
    subSbs: PropTypes.shape({
      id: PropTypes.string,
    }),
    instruction: PropTypes.string,
    detail: PropTypes.string,
    badges: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        src: PropTypes.string,
      }),
    ),
    stepConsumableUnits: PropTypes.arrayOf(
      PropTypes.shape({
        consumable: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        metricUnit: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
        quantity: PropTypes.number,
      }),
    ),
    media: PropTypes.object,
  }).isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func.isRequired,
};
